import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import { dateInput } from 'components/final-form/DateInput';
import DatePicker from 'components/final-form/DatePicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import { SelectFieldProps, selectInput } from 'components/final-form/SelectInput';
import { InputFieldProps, textInput } from 'components/final-form/TextInput';
import { timeFieldInput } from 'components/final-form/TimeFieldInput';
import { StyledTimeInput } from 'components/final-form/TimeInput';
import { InterventionAvailabilityType } from 'components/intervention-availability/InterventionAvailabilityView';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SaveActionButton from 'components/SaveActionButton';
import { FormApi, FormState } from 'final-form';
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import TimeField from 'react-simple-timefield';
import { Button, Grid, GridColumn, GridRow, Header, Table } from 'semantic-ui-react';
import { getAllContainers } from 'services/containerServices';
import {
  addInterventionAvailability,
  editInterventionAvailability,
  getInterventionAvailability
} from 'services/interventionAvailabilityServices';
import { getActiveInterventions } from 'services/interventionServices';
import { getAllShippableMedicaments } from 'services/medicamentServices';
import { getResources } from 'services/patientCentralServices';
import { searchInterventionAvailabilities } from 'services/slotManagementServices';
import styled from 'styled-components';
import { InterventionExternalCode, InterventionType } from 'ts-types/api.enums';
import {
  InterventionAvailabilityDetailsDto,
  InterventionAvailabilityDto,
  InterventionBlockedSlotDto,
  InterventionDto,
  MedicamentDto,
  ResourceDto,
  UpsertInterventionAvailabilityDto
} from 'ts-types/api.types';
import { isoToDisplayDate, isoToDisplayTime } from 'utils/dateUtils';
import { IndexSignature } from 'utils/praxTypes';
import { timeFromString } from 'utils/timeUtils';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';

const InterventionAvailabilityFormContainer = styled(FormContainer)`
  .ui.grid > .row.section {
    margin-top: 1.75rem;
    padding-top: 1rem !important;
    border-top: 1px solid #e0e0e0;
  }
`;

const ScrollableTableContainer = styled.div`
  height: 200px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34, 36, 38, .15);
  box-shadow: none;
  border-radius: .28571429rem;
  padding: 0 !important;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
    border-bottom: 1px solid rgba(34, 36, 38, .15)
  }
`;

const ButtonColumn = styled(Grid.Column)`
  & .ui.button {
    margin-top: .5rem;
    margin-bottom: 0;
  }
`;

interface ExtendedUpsertInterventionAvailabilityDto extends UpsertInterventionAvailabilityDto {
  interventionId: number,
  dayOfWeekArray: boolean[]
}

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  interventionAvailabilityId: number;
  interventionAvailabilityType: InterventionAvailabilityType;
  onSave: () => void;
  onCancel: () => void;
}

interface State {
  pageDataLoaded: boolean;
  errorMessage?: string;

  availabilityDto?: InterventionAvailabilityDetailsDto;
  availabilityEditable: boolean;

  resources: DropdownOption[];
  containerOptions: DropdownOption[];
  interventions: InterventionDto[];
  interventionOptions: DropdownOption[];
  medicaments: DropdownOption[];
  availabilities2ndDose: InterventionAvailabilityDto[];
  availabilities2ndDoseOptions: DropdownOption[];

  blockedSlotIx?: number;
  blockedSlot?: InterventionBlockedSlotDto;
  newBlockedSlot: boolean;
  errors: Array<boolean>;

  cancelTokenSource: CancelTokenSource;
}

class UpsertInterventionAvailabilitySlotForm extends Component<Props, State> {

  initialValues: Partial<ExtendedUpsertInterventionAvailabilityDto> = {
    active: false,
    selfService: false,
    addSecondVaccination: false,
    interventionIds: [],
    interventionBlockedSlots: [],
    dayOfWeekArray: [false, false, false, false, false, false, false]
  };

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      pageDataLoaded: false,
      availabilityEditable: true,
      resources: [],
      containerOptions: [],
      interventions: [],
      interventionOptions: [],
      medicaments: [],
      availabilities2ndDose: [],
      availabilities2ndDoseOptions: [],
      newBlockedSlot: false,
      errors: [false, false],
      cancelTokenSource
    }

    this.fetchPageData(props);
  };

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  fetchPageData = async (props: Props) => {

    const {
      interventionAvailabilityId,
      interventionAvailabilityType,
      currentUser,
      getMandate
    } = props;

    const {cancelTokenSource} = this.state;

    if (interventionAvailabilityId) {
      const availabilityDto = await getInterventionAvailability(interventionAvailabilityId, cancelTokenSource);

      const availabilityEditable: boolean = availabilityDto
          && availabilityDto.numberOfBookedSlots === 0
          && !availabilityDto.recurrent
          && _.isNil(availabilityDto.recurrentAvailabilityId);

      this.setState({
        availabilityDto: availabilityDto,
        availabilityEditable: availabilityEditable
      });

      const dayOfWeekArray = this.initialValues.dayOfWeekArray || [false, false, false, false, false, false, false];
      if (availabilityDto.recurOnDays) {
        availabilityDto.recurOnDays.forEach(num => dayOfWeekArray[num - 1] = true)
      }

      this.initialValues = {
        ...this.initialValues,
        ...availabilityDto,
        dayOfWeekArray
      };

      if (interventionAvailabilityType === InterventionAvailabilityType.VACCINATION) {
        this.initialValues = {
          ...this.initialValues,
          interventionId: availabilityDto.interventionIds && availabilityDto.interventionIds.length > 0
              ? availabilityDto.interventionIds[0]
              : undefined
        }
      }
    }

    const secondAvailabilitySearchRequest = {mandateId: getMandate(), withSelfService: true};
    const mandateId = currentUser!.userConfig.mandateId;


    Promise.all([
          getResources(getMandate(), cancelTokenSource),
          getActiveInterventions(cancelTokenSource),
          getAllShippableMedicaments(cancelTokenSource),
          searchInterventionAvailabilities(secondAvailabilitySearchRequest, cancelTokenSource),
          getAllContainers(mandateId, cancelTokenSource)
        ])
        .then(result => {

          const resources: ResourceDto[] = result[0];

          const vaccinationType = interventionAvailabilityType === InterventionAvailabilityType.VACCINATION;

          const interventions: InterventionDto[] = result[1].filter(
              inv => {
                if (vaccinationType) {
                  return inv.type === InterventionType.COVID_VACCINATION;
                }
                return true;
              });

          const interventionsMap = _.keyBy(interventions, "id");

          let medicaments: MedicamentDto[] = [];
          let medicamentOptions: DropdownOption[] = [];
          let availabilities2ndDose: InterventionAvailabilityDto[] = [];
          let availabilities2ndDoseOptions: DropdownOption[] = [];

          if (vaccinationType) {
            medicaments = result[2];

            medicamentOptions = mapToDropdownOptionArrayWithTextGetter(
                medicaments, medicamant => `${medicamant.code} - ${medicamant.description}`, "id");

            availabilities2ndDose = result[3].filter(avail => {
              const intervention = interventionsMap[avail.interventionIds[0]];
              return intervention && intervention.externalCode === InterventionExternalCode.VACCINATION_DOSE_2;
            });

            availabilities2ndDoseOptions = mapToDropdownOptionArrayWithTextGetter(
                availabilities2ndDose, avail =>
                    `${isoToDisplayDate(avail.date)} [${isoToDisplayTime(avail.fromTime)} - ${isoToDisplayTime(avail.toTime)}]`, "id");
          }

          const resourcesOptions = mapToDropdownOptionArray(resources, "description", "id");

          const interventionOptions = mapToDropdownOptionArray(interventions, "description", "id");

          const availability = this.state.availabilityDto;
          const containers = result[4].filter(container =>
              container.active || (availability && availability.containerIds &&
                  availability.containerIds.find(availabilityContainer => availabilityContainer === container.id)));
          const containerOptions = mapToDropdownOptionArray(containers, "id", "id");

          this.setState({
            resources: resourcesOptions,
            containerOptions,
            interventions: interventions,
            interventionOptions: interventionOptions,
            medicaments: medicamentOptions,
            availabilities2ndDose,
            availabilities2ndDoseOptions
          });

        })
        .finally(() => {
          this.setState({
            pageDataLoaded: true
          });
        });
  }

  isFirstDose = (values: any) => {

    if (this.props.interventionAvailabilityType === InterventionAvailabilityType.OTHER) {
      return false;
    }

    const {interventions} = this.state;

    const interventionId = values.interventionId;
    let dose1 = false;
    if (interventionId) {
      const selectedIntervention = interventions.find(inv => inv.id === interventionId);
      dose1 = !!selectedIntervention
          && selectedIntervention.externalCode === InterventionExternalCode.VACCINATION_DOSE_1;
    }

    return dose1;
  }

  handleChange = ({values}: FormState, form: FormApi) => {

    if (this.props.interventionAvailabilityType === InterventionAvailabilityType.OTHER) {
      return;
    }

    const dose1 = this.isFirstDose(values);

    if (dose1) {
      const firstDoseDate = values.date && new Date(values.date);
      let secondDoseDate = values.secondVaccinationDate ? new Date(values.secondVaccinationDate) : undefined;

      const setDate = () => {
        const newDate = values.date ? moment(values.date).add(4, "w") : moment();
        form.change("secondVaccinationDate", newDate.format("YYYY-MM-DD"));
        secondDoseDate = new Date(values.secondVaccinationDate);
      }

      if (firstDoseDate && secondDoseDate === undefined) {
        setDate();
      }

      if (firstDoseDate && secondDoseDate) {
        const newDate = moment(firstDoseDate).add(4, "w").toDate();
        if (newDate.getTime() > secondDoseDate.getTime()) {
          setDate();
        }
      }

      if (values.addSecondVaccination) {
        form.change("secondDoseAvailabilityId", undefined);
      }

    } else {
      form.change("addSecondVaccination", false);
      form.change("secondVaccinationDate", undefined);
      form.change("secondVaccinationLotNumber", undefined);
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  validateToTime = (fromTimeString: string, toTimeString: string) => {
    const fromTime = timeFromString(fromTimeString);
    const toTime = timeFromString(toTimeString);

    if (!fromTime || !toTime) {
      return true;
    }

    return fromTime.compare(toTime) < 0;
  }

  handleSubmit = async (values: Partial<ExtendedUpsertInterventionAvailabilityDto>) => {
    const {interventionAvailabilityId, onSave, getMandate, getMessage} = this.props;
    const {interventions, cancelTokenSource} = this.state;

    const validToTime = this.validateToTime(values.fromTime!, values.toTime!);
    if (!validToTime) {
      this.setErrorMessage(getMessage("error.INVALID_TO_TIME"));
      return;
    }

    let dose1 = this.isFirstDose(values);

    const recurOnDays: number[] = [];
    if (values.recurrent) {
      values.dayOfWeekArray!.forEach((dayFlag, ix) => {
        if (dayFlag) {
          recurOnDays.push(ix + 1);
        }
      });
    }

    const upsertDto: Partial<UpsertInterventionAvailabilityDto> = {
      ...values,
      mandateId: getMandate(),
      recurOnDays: recurOnDays,
      interventionIds: values.interventionId !== undefined ? [values.interventionId] : values.interventionIds,
      secondDoseAvailabilityId: dose1 ? values.secondDoseAvailabilityId : undefined,
      addSecondVaccination: dose1 ? values.addSecondVaccination : false,
      secondVaccinationDate: dose1 ? values.secondVaccinationDate : undefined,
      secondVaccinationLotNumber: dose1 ? values.secondVaccinationLotNumber : undefined
    };

    if (interventionAvailabilityId) {
      await editInterventionAvailability(interventionAvailabilityId, upsertDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await addInterventionAvailability(upsertDto, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => this.handleError(e.response.data))
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  validateFields = (values: any): boolean => {
    const {blockedSlot} = this.state;

    let errors: Array<boolean> = [];

    errors[0] = blockedSlot!.fromTime == "";
    errors[1] = blockedSlot!.toTime == "";

    this.setState({
      errors: errors
    });

    return errors.includes(true);
  };

  addNewBlockedSlot = (values: any, form: FormApi) => {

    let interventionBlockedSlots = [...values.interventionBlockedSlots];

    let fromTime = moment();
    const splitFromTime = interventionBlockedSlots[interventionBlockedSlots.length - 1]
        ? interventionBlockedSlots[interventionBlockedSlots.length - 1].toTime.split(/:/)
        : values.fromTime.split(/:/);
    fromTime.hours(parseInt(splitFromTime[0])).minutes(parseInt(splitFromTime[1])).seconds(0).milliseconds(0);

    let toTime = moment();
    const splitToTime = values.toTime.split(/:/);
    toTime.hours(parseInt(splitToTime[0])).minutes(parseInt(splitToTime[1])).seconds(0).milliseconds(0);

    const startTime = moment(fromTime).add(1, 'hours');

    if (startTime.isSameOrBefore(toTime)) {
      fromTime = startTime;
      const endTime = moment(fromTime).add(15, 'minutes');
      if (endTime.isSameOrBefore(toTime)) {
        toTime = endTime
      }
    } else {
      const endTime = moment(values.fromTime).add(15, 'minutes');
      if (endTime.isSameOrBefore(toTime)) {
        toTime = endTime
      }
    }

    const newBlockedSlot = {
      fromTime: fromTime.format("HH:mm"),
      toTime: toTime.format("HH:mm")
    };

    interventionBlockedSlots.push(newBlockedSlot);

    form.change("interventionBlockedSlots", interventionBlockedSlots);

    this.setState({
      blockedSlotIx: interventionBlockedSlots.length - 1,
      blockedSlot: newBlockedSlot,
      newBlockedSlot: true
    });
  }

  editBlockedSlot = (values: any, index: number) => {
    const interventionBlockedSlots = [...values.interventionBlockedSlots];

    const blockedSlot: InterventionBlockedSlotDto = interventionBlockedSlots[index];

    if (blockedSlot) {
      this.setState({
        blockedSlotIx: index,
        blockedSlot: blockedSlot
      });
    }
  };

  deleteBlockedSlot = (values: any, form: FormApi, index: number) => {
    let interventionBlockedSlots = [...values.interventionBlockedSlots];

    if (interventionBlockedSlots[index]) {
      interventionBlockedSlots = interventionBlockedSlots.filter((slot, ix) => {
        return index != ix;
      });
      form.change("interventionBlockedSlots", interventionBlockedSlots)
    }
  };

  saveBlockedSlot = (values: any, form: FormApi, index: number) => {

    const {blockedSlot} = this.state;

    let interventionBlockedSlots = [...values.interventionBlockedSlots];

    if (interventionBlockedSlots[index] && !this.validateFields(values)) {

      interventionBlockedSlots[index] = blockedSlot;

      interventionBlockedSlots.sort(function (a, b) {
        return a.fromTime.localeCompare(b.fromTime);
      });

      form.change("interventionBlockedSlots", interventionBlockedSlots);

      this.setState({
        blockedSlotIx: undefined,
        blockedSlot: undefined,
        newBlockedSlot: false
      });
    }
  };

  cancelEditBlockedSlot = (values: any, form: FormApi) => {
    const {newBlockedSlot} = this.state;

    if (newBlockedSlot) {
      let interventionBlockedSlots = [...values.interventionBlockedSlots];
      interventionBlockedSlots.pop();
      form.change("interventionBlockedSlots", interventionBlockedSlots);
    }

    this.setState({
      blockedSlotIx: undefined,
      blockedSlot: undefined,
      newBlockedSlot: false
    });
  };

  onFromTimeChange = (e: any) => {
    e.persist();

    //@ts-ignore
    this.setState(({blockedSlot}: State) => ({
      blockedSlot: {
        ...blockedSlot,
        fromTime: e.target.value
      }
    }));
  };

  onToTimeChange = (e: any) => {
    e.persist();

    //@ts-ignore
    this.setState(({blockedSlot}: State) => ({
      blockedSlot: {
        ...blockedSlot,
        toTime: e.target.value
      }
    }));
  };

  render(): React.ReactNode {

    if (!this.state.pageDataLoaded) {
      return <></>;
    }

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{pristine: true, submitting: true, values: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({form, handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <InterventionAvailabilityFormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                {this.renderBasicInformationSection(form)}
                {this.renderContainerSection(form)}
                {this.renderInterventionSection(form)}
                {this.renderBlockedSlotsSection(form, values)}
                {this.renderSecondDoseSection(form, values)}
                {this.renderActivationSection(form)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </InterventionAvailabilityFormContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </form>
    );
  };

  renderBasicInformationSection = (form: FormApi): React.ReactNode => {

    const {interventionAvailabilityId, interventionAvailabilityType, getMessage} = this.props;
    const {availabilityEditable, resources} = this.state;

    const editDisabled = !availabilityEditable;

    const editMode = !!interventionAvailabilityId;
    const vaccinationType = interventionAvailabilityType === InterventionAvailabilityType.VACCINATION;

    const selectMap: IndexSignature = {
      resourceId: resources
    };

    const values = form.getState().values;

    let basicLeftInfoFields = [
      dateInput({
        name: "date",
        disabled: editDisabled,
        validate: required
      }),
      selectInput({
            name: "resourceId",
            allowAdditions: false,
            disabled: editDisabled,
            validate: required
          },
          selectMap
      )
    ];
    const recurrent = values.recurrent;
    if (recurrent && !vaccinationType) {
      basicLeftInfoFields = [
        dateInput({
          name: "fromDate",
          disabled: editDisabled,
          validate: required
        }),
        dateInput({
          name: "toDate",
          disabled: editDisabled,
          validate: required
        }),
        selectInput({
              name: "resourceId",
              allowAdditions: false,
              disabled: editDisabled,
              validate: required
            },
            selectMap
        )
      ];
    }

    const basicRightInfoFields = [
      timeFieldInput({
        name: "fromTime",
        size: "small",
        disabled: editDisabled,
        validate: required
      }),
      timeFieldInput({
        name: "toTime",
        size: "small",
        disabled: editDisabled,
        validate: required
      })
    ];

    const mappedLeftRow = this.renderFieldRows(basicLeftInfoFields);
    const mappedRightRow = this.renderFieldRows(basicRightInfoFields);

    return (
        <React.Fragment>
          {
              !vaccinationType &&
              <Grid.Row>
                <Grid.Column width={8}>
                  <Field
                      label={getMessage("interventionAvailability.form.recurrent")}
                      name="recurrent"
                      component={CheckBox}
                      disabled={editMode}
                  />
                </Grid.Column>
              </Grid.Row>
          }
          {
              recurrent &&
              <Grid.Row>
                <Grid.Column width={8}>
                  {
                    [1, 2, 3, 4, 5, 6, 7].map(day => {
                      return <Field
                          key={`day${day}-check`}
                          label={moment(`${day}`, "E").format("ddd")}
                          name={`dayOfWeekArray[${day - 1}]`}
                          component={CheckBox}
                          disabled={editDisabled}
                      />
                    })
                  }
                </Grid.Column>
              </Grid.Row>
          }
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderFieldRows = (basicRightInfoFields: any[]) => {

    const {getMessage} = this.props;

    const mappedRows = basicRightInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(`interventionAvailability.form.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return mappedRows;
  }

  renderContainerSection = (form: FormApi): React.ReactNode => {

    const {availabilityDto, availabilityEditable, containerOptions} = this.state;
    const {getMessage} = this.props;

    const editable = availabilityEditable
        || (availabilityDto && (availabilityDto!.recurrent || !_.isNil(availabilityDto!.recurrentAvailabilityId)));
    const editDisabled = !editable;

    return (
        <React.Fragment>
          <Grid.Row className="section">
            <Grid.Column width={16}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {`${getMessage(`interventionAvailability.form.containerIds`)}:`}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        name="containerIds"
                        component={Select}
                        options={containerOptions}
                        multiple
                        allowAdditions={false}
                        disabled={editDisabled}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  }

  renderInterventionSection = (form: FormApi): React.ReactNode => {

    const {availabilityDto, availabilityEditable, interventionOptions, medicaments} = this.state;
    const {interventionAvailabilityType, getMessage} = this.props;

    const validator = (value: string) => required(value);

    const editable = availabilityEditable
        || (availabilityDto && (availabilityDto!.recurrent || !_.isNil(availabilityDto!.recurrentAvailabilityId)));
    const editDisabled = !editable;

    const vaccinationType = interventionAvailabilityType === InterventionAvailabilityType.VACCINATION;

    const selectMap: IndexSignature = {
      medicamentId: medicaments
    };

    let basicRightInfoFields: (SelectFieldProps | InputFieldProps)[] = [];

    if (vaccinationType) {
      basicRightInfoFields = [
        selectInput({
              name: "medicamentId",
              allowAdditions: false,
              disabled: editDisabled,
              validate: validator
            },
            selectMap
        ),
        textInput({
          name: "lotNumber",
          disabled: editDisabled,
          validate: validator
        }),
        textInput({
          name: "numberOfDoses",
          disabled: editDisabled,
          validate: validator
        }),
        textInput({
          name: "minimumAge",
          disabled: editDisabled,
          validate: composeValidators(mustBeNumber, mustBePositiveNumber)
        })
      ]
    }

    const leftFieldName = vaccinationType ? "interventionId" : "interventionIds";

    const mappedRightRow = this.renderFieldRows(basicRightInfoFields);

    return (
        <React.Fragment>
          <Grid.Row className="section">
            <Grid.Column width={vaccinationType ? 8 : 16}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {`${getMessage(`interventionAvailability.form.${leftFieldName}`)}:`}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        name={leftFieldName}
                        component={Select}
                        options={interventionOptions}
                        multiple={interventionAvailabilityType === InterventionAvailabilityType.OTHER}
                        allowAdditions={false}
                        disabled={editDisabled}
                        validate={validator}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Grid.Column>
            {vaccinationType &&
                <Grid.Column width={8}>
                  <PraxFormGrid>
                    {mappedRightRow}
                  </PraxFormGrid>
                </Grid.Column>
            }
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderActivationSection = (form: FormApi): React.ReactNode => {

    const {getMessage} = this.props;

    const values: UpsertInterventionAvailabilityDto = form.getState().values as UpsertInterventionAvailabilityDto;

    let dose1 = this.isFirstDose(values);

    return (
        <React.Fragment>
          <Grid.Row className="section">
            {/*<Grid.Column width={3} verticalAlign="middle">
              `${getMessage("interventionAvailability.form.active")}:`
            </Grid.Column>*/}
            <Grid.Column width={8}>
              <Field
                  label={getMessage("interventionAvailability.form.active")}
                  name="active"
                  component={CheckBox}
                  toggle
              />
            </Grid.Column>
            {/*<Grid.Column width={3} verticalAlign="middle">
              `${getMessage("interventionAvailability.form.selfService")}:`
            </Grid.Column>*/}
            <Grid.Column width={8}>
              <PraxFormGrid>
                <GridRow>
                  <GridColumn width={16}>
                    <Field
                        label={getMessage("interventionAvailability.form.selfService")}
                        name="selfService"
                        component={CheckBox}
                        toggle
                    />
                  </GridColumn>
                </GridRow>
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderBlockedSlotsSection = (form: FormApi, values: any): React.ReactNode => {

    const {getMessage} = this.props;
    const {availabilityEditable, blockedSlot, blockedSlotIx, errors} = this.state;

    const blockedSlots = values.interventionBlockedSlots ? values.interventionBlockedSlots : [];

    const disableAdd = !((values.fromTime !== undefined && values.fromTime.length > 0)
        && (values.toTime !== undefined && values.toTime.length > 0));

    return <React.Fragment>
      <Grid.Row className="section">
        <Grid.Column>
          <Header as="h4">
            {getMessage("interventionAvailability.form.blockedSlots")}
          </Header>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          <ScrollableTableContainer>
            <Table celled selectable size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{getMessage('interventionAvailability.form.fromTime')}</Table.HeaderCell>
                  <Table.HeaderCell>{getMessage('interventionAvailability.form.toTime')}</Table.HeaderCell>
                  <Table.HeaderCell textAlign={"center"}>{getMessage('actions')}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {
                  blockedSlots.map((slot: InterventionBlockedSlotDto, index: number) => {
                    const editing = blockedSlotIx === index;
                    return (
                        <Table.Row key={index}>
                          <Table.Cell>
                            {editing && availabilityEditable
                                ? <TimeField
                                    value={blockedSlot!.fromTime}
                                    onChange={(e: any) => this.onFromTimeChange(e)}
                                    input={
                                      <StyledTimeInput
                                          meta={{touched: errors[0], error: errors[0]}}
                                          size="small"
                                      />
                                    }
                                />
                                : slot.fromTime
                            }
                          </Table.Cell>
                          <Table.Cell>
                            {editing && availabilityEditable
                                ? <TimeField
                                    value={blockedSlot!.toTime}
                                    onChange={(e: any) => this.onToTimeChange(e)}
                                    input={
                                      <StyledTimeInput
                                          meta={{touched: errors[1], error: errors[1]}}
                                          size="small"
                                      />
                                    }
                                />
                                : slot.toTime
                            }
                          </Table.Cell>
                          <Table.Cell width={2} textAlign={"center"}>
                            {editing && availabilityEditable
                                ? <React.Fragment>
                                  <SaveActionButton hoverMessage={getMessage('button.save')}
                                                    onConfirm={() => this.saveBlockedSlot(values, form, index)}
                                  />
                                  <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                      onConfirm={() => this.cancelEditBlockedSlot(values, form)}
                                  />
                                </React.Fragment>
                                : <React.Fragment>
                                  <EditActionButton
                                      hoverMessage={getMessage('msgEdit')}
                                      onConfirm={() => this.editBlockedSlot(values, index)}
                                      disabled={!availabilityEditable}
                                  />
                                  <DeleteActionButton
                                      hoverMessage={getMessage("msgDelete")}
                                      onConfirm={() => this.deleteBlockedSlot(values, form, index)}
                                      disabled={!availabilityEditable}
                                  />
                                </React.Fragment>
                            }
                          </Table.Cell>
                        </Table.Row>
                    )
                  })
                }
              </Table.Body>
            </Table>
          </ScrollableTableContainer>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <ButtonColumn>
          <Button
              type="button"
              compact
              onClick={() => this.addNewBlockedSlot(values, form)}
              disabled={blockedSlotIx !== undefined || !availabilityEditable || disableAdd}
          >
            {getMessage('button.add')}
          </Button>
        </ButtonColumn>
      </Grid.Row>
    </React.Fragment>;
  };

  renderSecondDoseSection = (form: FormApi, values: any): React.ReactNode => {

    const {interventionAvailabilityType, getMessage} = this.props;
    const {availabilities2ndDoseOptions} = this.state;

    const vaccinationType = interventionAvailabilityType === InterventionAvailabilityType.VACCINATION;
    if (!vaccinationType) {
      return <></>;
    }

    let dose1 = this.isFirstDose(values);

    const secondDoseDate = values.date ? moment(values.date).add(4, "w") : moment();
    const disabledBefore = secondDoseDate.toDate();

    return (
        <React.Fragment>
          <Grid.Row className="section">
            <Grid.Column width={8}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="middle">
                    <Field
                        name="addSecondVaccination"
                        component={CheckBox}
                        label={getMessage("interventionAvailability.form.addSecondVaccination")}
                        disabled={!dose1}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Grid.Column>
            {
                dose1 && !values.addSecondVaccination &&
                <Grid.Column width={8}>
                  <PraxFormGrid>
                    <GridRow>
                      <GridColumn width={5}>
                        <label htmlFor="availability2ndDose">
                          {getMessage("interventionAvailability.form.availability2ndDose")}
                        </label>
                      </GridColumn>
                      <GridColumn width={11}>
                        <Field
                            name="secondDoseAvailabilityId"
                            component={Select}
                            options={availabilities2ndDoseOptions}
                            // validate={oneBlockProcedurePerTariff(tariffs, blockProcedureMap)}
                            fluid
                        />
                      </GridColumn>
                    </GridRow>
                  </PraxFormGrid>
                </Grid.Column>
            }
          </Grid.Row>
          {
              values.addSecondVaccination &&
              <Grid.Row>
                <Grid.Column width={8}>
                  <PraxFormGrid>
                    <Grid.Row>
                      <Grid.Column width={5} verticalAlign="middle">
                        {getMessage("interventionAvailability.form.date")}:
                      </Grid.Column>
                      <Grid.Column width={11} verticalAlign="middle">
                        <Field
                            name="secondVaccinationDate"
                            component={DatePicker}
                            disabledBefore={disabledBefore}
                            disabled={!dose1}
                            fluid
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </PraxFormGrid>
                </Grid.Column>
                <Grid.Column width={8}>
                  <PraxFormGrid>
                    <Grid.Row>
                      <Grid.Column width={5} verticalAlign="middle">
                        {getMessage("interventionAvailability.form.lotNumber")}:
                      </Grid.Column>
                      <Grid.Column width={11} verticalAlign="middle">
                        <Field
                            name="secondVaccinationLotNumber"
                            component={Input}
                            disabled={!dose1}
                            fluid
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </PraxFormGrid>
                </Grid.Column>
              </Grid.Row>
          }
        </React.Fragment>
    );
  }

}

export default withAuthContext(withMessages(UpsertInterventionAvailabilitySlotForm));
